/* ---------------------------------------------------------------------------
   The construction programme of an imported model, from the export's own parts.

   ══ WHY NOT THE MODELLER'S SCHEDULE ══

   The modeller extracts its schedule from its PARAMETRIC tree. An imported model
   is drawn from the export's mesh, and the parametric tree behind it is whatever
   the modeller booted with, patched by the import where a setting maps: on the
   2026-09-30 QA run a 3-span, 2-bent overpass with 4 piles a footing froze a
   263-activity programme with 4 spans, 3 bents, 1 column a bent and piles P-1 …
   P-6. Its names were not the components the site records against either — P-1
   against Pile-1, B-1 (in) against BA-1 — so "Match all" matched 32 of 204.

   The export already says what is built: every mesh part has a name, a type and
   the source's own id, and those are exactly the components the register's 3D
   view offers (functions/lib/meshGlb.ts writes them). So a programme for an
   imported model is one activity per component, placed on the support or span it
   belongs to, sequenced the way a girder bridge is built. Each activity carries
   its component and id, so matching the programme to the model confirms an
   identity rather than guessing at a name.

   ══ WHERE A PART BELONGS ══

   In this order, and the first that answers wins:

     0. The export's component directory, where it has one: the span or support
        each component is on.
     1. An id that is a path, "Span-2/Girder-3", "Bent-1/Foundation-1/Pile-3":
        its first step names the unit.
     2. An owner or id that is a support's, a cap's, a column's or a foundation's
        `UiID` in the export's Bents block.
     3. Where it is along the bridge: the part's centroid, as a station, against
        the supports' stations — a substructure part to the nearest support, a
        superstructure part to the span it lies in.

   ══ WHAT IS DELIBERATELY SIMPLE ══

   Durations are working-day defaults per kind of part, and the logic is
   finish-to-start. It is a programme to baseline and measure against, not a
   method statement: a planner who wants a different sequence builds one in the
   modeller's Schedule view on a parametric model.
   --------------------------------------------------------------------------- */

import type { SerialisedSchedule } from './programme.ts';
import { componentsOf } from './modelComponents.ts';

/** What an activity builds: the component as the 3D view names it, and its id. */
export interface ProgrammeComponent {
  component: string;
  componentId: string | null;
}

export interface ImportedProgramme {
  schedule: SerialisedSchedule;
  /** Activity id to the component it builds. Leaves only. */
  components: Record<string, ProgrammeComponent>;
}

type Stage =
  | 'pile'
  | 'footing'
  | 'column'
  | 'front-wall'
  | 'wall'
  | 'cap'
  | 'bearing'
  | 'approach'
  | 'girder'
  | 'diaphragm'
  | 'deck'
  | 'edge'
  | 'surfacing'
  | 'other';

/** Working days per part, by what it is. */
const DURATION: Record<Stage, number> = {
  pile: 3,
  footing: 7,
  column: 8,
  'front-wall': 10,
  wall: 6,
  cap: 10,
  bearing: 1,
  approach: 5,
  girder: 1,
  diaphragm: 1,
  deck: 8,
  edge: 4,
  surfacing: 3,
  other: 2,
};

/** The modeller's kind strings where one fits, so the Gantt and the site record read the same. */
const KIND: Record<Stage, string> = {
  pile: 'Pile',
  footing: 'Foundation',
  column: 'Column',
  'front-wall': 'Wall',
  wall: 'Wall',
  cap: 'Bent Cap',
  bearing: 'Bearing',
  approach: 'Approach Slab',
  girder: 'Girder',
  diaphragm: 'Diaphragm',
  deck: 'Deck Section',
  edge: 'Barrier',
  surfacing: 'Road Surface',
  other: '',
};

const SUBSTRUCTURE: ReadonlySet<Stage> = new Set(['pile', 'footing', 'column', 'front-wall', 'wall', 'cap', 'bearing', 'approach']);

interface Part {
  name: string;
  id: string | null;
  owner: string | null;
  type: string;
  x: number;
  z: number;
}

interface Support {
  name: string;
  station: number;
  abutment: boolean;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const rec = (v: unknown): Record<string, unknown> => (typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {});

/** What a part is, from its type and name. */
export function stageOf(type: string, name: string): Stage {
  const t = type.toLowerCase();
  const n = name.toLowerCase();
  if (t === 'foundation') return /pile/.test(n) ? 'pile' : 'footing';
  if (t === 'column') return 'column';
  if (t === 'bentcap') return 'cap';
  if (t === 'pierbent') return 'bearing';
  if (t === 'abutment') {
    if (/^b[ab]-\d+$/.test(n) || /bearing(?! beam)/.test(n)) return 'bearing';
    if (/bearing beam|cap/.test(n)) return 'cap';
    if (/front wall/.test(n)) return 'front-wall';
    if (/wall/.test(n)) return 'wall';
    return 'approach';
  }
  if (t === 'pscgirder' || /girder|beam/.test(t)) return 'girder';
  if (t === 'diaphragm') return 'diaphragm';
  if (t === 'deckslab' || t === 'deck') return 'deck';
  if (t === 'barrier' || t === 'walkway' || /railing|parapet|footpath/.test(t)) return 'edge';
  if (t === 'trafficlane' || /surfac|lane/.test(t)) return 'surfacing';
  return 'other';
}

/** The parts of the export's mesh, each with its plan centroid in model millimetres. */
function partsOf(doc: Record<string, unknown>): Part[] {
  const mesh = rec(doc['Mesh']);
  return arr(mesh['Parts']).flatMap((raw): Part[] => {
    const p = rec(raw);
    const name = str(p['name']);
    const type = str(p['type']) ?? '';
    const pos = arr(p['positions']);
    if (name === null || pos.length < 3) return [];
    let sx = 0;
    let sz = 0;
    let count = 0;
    for (let i = 0; i + 2 < pos.length; i += 3) {
      const x = num(pos[i]);
      const z = num(pos[i + 2]);
      if (x === null || z === null) continue;
      sx += x;
      sz += z;
      count++;
    }
    if (count === 0) return [];
    /* The component it draws: its GUID where the export gives one (migration 0049). */
    return [{ name, id: str(p['guid']) ?? str(p['id']), owner: str(p['owner']), type, x: (sx / count) * 1000, z: (sz / count) * 1000 }];
  });
}

/** The supports, abutments included, in station order. From the Bents block, else the span lengths. */
function supportsOf(doc: Record<string, unknown>): Support[] {
  const bents = arr(doc['Bents']).map(rec);
  const out: Support[] = [];
  for (const b of bents) {
    const name = str(b['Name']);
    const station = num(b['Station']);
    if (name === null || station === null) continue;
    out.push({ name, station, abutment: /abutment/i.test(str(b['Kind']) ?? name) });
  }
  if (out.length >= 2) return out.sort((a, b) => a.station - b.station);

  const bridge = rec(doc['Bridge']);
  const spans = arr(bridge['SpanLengths']).map(num).filter((v): v is number => v !== null && v > 0);
  if (spans.length === 0) return [];
  let station = num(bridge['StartStation']) ?? 0;
  const made: Support[] = [{ name: 'Abutment-1', station, abutment: true }];
  spans.forEach((len, i) => {
    station += len;
    const last = i === spans.length - 1;
    made.push({ name: last ? 'Abutment-2' : `Bent-${i + 1}`, station, abutment: last });
  });
  return made;
}

/** Every UiID the Bents block names, to the support it belongs to. */
function ownersOf(doc: Record<string, unknown>): Map<string, string> {
  const out = new Map<string, string>();
  for (const b of arr(doc['Bents']).map(rec)) {
    const name = str(b['Name']);
    if (name === null) continue;
    const ids = [
      b['UiID'],
      b['Id'],
      rec(b['CapBeam'])['UiID'],
      ...arr(b['Columns']).map((c) => rec(c)['UiID']),
      ...arr(b['Foundations']).map((f) => rec(f)['UiID']),
    ];
    for (const id of ids) {
      const s = str(id);
      if (s !== null) out.set(s, name);
    }
  }
  return out;
}

/** Station along the bridge, in model mm, from a plan position: off the centreline where there is one. */
function stationer(doc: Record<string, unknown>): (x: number, z: number) => number {
  const points = arr(rec(doc['Centerline'])['Points'])
    .map(rec)
    .map((p) => ({ s: num(p['S']), x: num(p['X']), y: num(p['Y']) }))
    .filter((p): p is { s: number; x: number; y: number } => p.s !== null && p.x !== null && p.y !== null);
  const start = num(rec(doc['Bridge'])['StartStation']) ?? 0;
  if (points.length < 2) return (x) => start + x;
  /* The viewer is Y-up, so the model's plan Y is the viewer's -Z; either sign is
     tried because a straight bridge cannot tell, and a curved one can. */
  return (x, z) => {
    let best = Number.POSITIVE_INFINITY;
    let station = start + x;
    for (const p of points) {
      for (const y of [-z, z]) {
        const d = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (d < best) {
          best = d;
          station = p.s;
        }
      }
    }
    return station;
  };
}

const UNIT = /^(Span|Bent|Abutment|Pier)-\d+$/;

/**
 * The programme of an imported model, or null where the export has no parts to
 * build one from. `texts` is the stored source: one JSON document per file.
 */
export function programmeFromExport(texts: readonly string[]): ImportedProgramme | null {
  const doc = texts
    .map((t) => {
      try {
        return rec(JSON.parse(t));
      } catch {
        return {};
      }
    })
    .find((d) => d['Mesh'] !== undefined);
  if (!doc) return null;

  const parts = partsOf(doc);
  const supports = supportsOf(doc);
  if (parts.length === 0 || supports.length < 2) return null;
  const owners = ownersOf(doc);
  const stationOf = stationer(doc);
  const spanNames = supports.slice(1).map((_, i) => `Span-${i + 1}`);
  /* Where the export's component directory says a part is (migration 0049). */
  const directory = new Map(componentsOf(doc).map((c) => [c.id, c]));

  /* One component per id, where the export gives one — a bearing drawn as a pad
     and a plate is one bearing — and per name within its unit where it does not. */
  interface Item {
    unit: string;
    stage: Stage;
    component: string;
    componentId: string | null;
    order: number;
  }
  const items: Item[] = [];
  const seen = new Map<string, Item>();

  const unitOf = (p: Part, stage: Stage): string => {
    const placed = p.id ? directory.get(p.id) : undefined;
    if (placed?.span != null && spanNames[placed.span - 1]) return spanNames[placed.span - 1]!;
    if (placed?.support != null && supports[placed.support - 1]) return supports[placed.support - 1]!.name;
    const head = p.id?.includes('/') ? p.id.split('/')[0]! : null;
    if (head && (UNIT.test(head) || supports.some((s) => s.name === head))) return head;
    const owned = (p.owner && owners.get(p.owner)) || (p.id && owners.get(p.id));
    if (owned) return owned;
    if (stage === 'surfacing') return 'Deck finishes';
    const s = stationOf(p.x, p.z);
    if (SUBSTRUCTURE.has(stage)) {
      let near = supports[0]!;
      for (const sp of supports) if (Math.abs(sp.station - s) < Math.abs(near.station - s)) near = sp;
      return near.name;
    }
    for (let i = 1; i < supports.length; i++) if (s <= supports[i]!.station) return spanNames[i - 1]!;
    return spanNames[spanNames.length - 1]!;
  };

  parts.forEach((p, order) => {
    const stage = stageOf(p.type, p.name);
    const unit = unitOf(p, stage);
    const key = p.id ?? `${unit}|${p.type}|${p.name}`;
    if (seen.has(key)) return;
    const item: Item = { unit, stage, component: p.name, componentId: p.id, order };
    seen.set(key, item);
    items.push(item);
  });

  /* ---- the tree: units under Substructure / Superstructure / Deck finishes ---- */
  const activities: NonNullable<SerialisedSchedule['activities']> = [];
  const links: NonNullable<SerialisedSchedule['links']> = [];
  const components: Record<string, ProgrammeComponent> = {};
  let seq = 1000;
  const nextId = (): string => `A${(seq += 10)}`;
  const group = (name: string, parentId: string | null): string => {
    const id = nextId();
    activities.push({ id, name, parentId, children: [], durationDays: 0, manualStartDays: null, componentKind: '' });
    if (parentId !== null) activities.find((a) => a.id === parentId)!.children!.push(id);
    return id;
  };
  const leaf = (it: Item, name: string, parentId: string): string => {
    const id = nextId();
    activities.push({ id, name, parentId, children: [], durationDays: DURATION[it.stage], manualStartDays: null, componentKind: KIND[it.stage] });
    activities.find((a) => a.id === parentId)!.children!.push(id);
    components[id] = { component: it.component, componentId: it.componentId };
    return id;
  };
  const fs = (from: string | undefined, to: string | undefined): void => {
    if (from && to && from !== to) links.push({ from, to, type: 'FS', lagDays: 0 });
  };

  const root = group('Bridge', null);
  const sub = group('Substructure', root);
  const sup = group('Superstructure', root);

  const nameOfId = new Map(items.flatMap((it) => (it.componentId ? [[it.componentId, it.component] as const] : [])));
  const nameCount = new Map<string, number>();
  for (const it of items) nameCount.set(`${it.unit}|${it.component}`, (nameCount.get(`${it.unit}|${it.component}`) ?? 0) + 1);
  /** An id's last step, as words: "Span-1/LeftBarrier" reads "Left Barrier". */
  const lastStep = (id: string | null): string | null =>
    id?.includes('/') ? id.split('/').pop()!.replace(/([a-z])([A-Z])/g, '$1 $2') : null;
  /** What a part is called on the programme: its own name, unless that name only says which span it is on. */
  const baseName = (it: Item): string => {
    if (it.component === it.unit || /^PSC |Girders-\d/.test(it.component)) {
      return lastStep(it.componentId) ?? (KIND[it.stage] || it.component);
    }
    /* "Pile-1" under F-1 and under F-2 read "Pile-1 (F-1)" and "Pile-1 (F-2)". */
    const shared = (nameCount.get(`${it.unit}|${it.component}`) ?? 0) > 1;
    const parent = it.componentId?.includes('/') ? it.componentId.slice(0, it.componentId.lastIndexOf('/')) : null;
    const owner = parent !== null ? nameOfId.get(parent) : undefined;
    return shared && owner !== undefined ? `${it.component} (${owner})` : it.component;
  };

  /** Names within a unit, numbered where two would still read the same. */
  const named = (list: Item[]): Map<Item, string> => {
    const out = new Map<Item, string>();
    const counts = new Map<string, number>();
    for (const it of list) {
      const base = baseName(it);
      counts.set(base, (counts.get(base) ?? 0) + 1);
    }
    const used = new Map<string, number>();
    for (const it of list) {
      const base = baseName(it);
      if ((counts.get(base) ?? 0) > 1) {
        const n = (used.get(base) ?? 0) + 1;
        used.set(base, n);
        out.set(it, `${base} ${n}`);
      } else out.set(it, base);
    }
    return out;
  };
  const byStage = (list: Item[], stage: Stage): Item[] => list.filter((i) => i.stage === stage).sort((a, b) => a.order - b.order);

  /* ---- substructure, one unit per support ---- */
  const bearingsDone = new Map<string, string[]>();
  let lastApproach: string[] = [];
  for (const s of supports) {
    const list = items.filter((i) => i.unit === s.name);
    if (list.length === 0) continue;
    const gid = group(s.name, sub);
    const names = named(list);
    const ids = new Map<Item, string>();
    for (const it of [...list].sort((a, b) => a.order - b.order)) ids.set(it, leaf(it, names.get(it)!, gid));
    const idsOf = (stage: Stage): string[] => byStage(list, stage).map((i) => ids.get(i)!);

    const chain = (xs: string[]): void => xs.forEach((x, i) => fs(xs[i - 1], x));
    const after = (before: string[], xs: string[]): void => {
      for (const x of xs) for (const b of before) fs(b, x);
    };
    /* One piling rig: every pile of the support in turn. A footing waits for
       its own piles where the ids say which they are, else for the last. */
    const pileItems = byStage(list, 'pile');
    const piles = pileItems.map((i) => ids.get(i)!);
    chain(piles);
    const footingItems = byStage(list, 'footing');
    const footings = footingItems.map((i) => ids.get(i)!);
    for (const f of footingItems) {
      const own = pileItems.filter((p) => f.componentId !== null && p.componentId?.startsWith(`${f.componentId}/`));
      after(own.length ? own.slice(-1).map((p) => ids.get(p)!) : piles.slice(-1), [ids.get(f)!]);
    }
    /* Column n on footing n where there are as many of each; else on them all. */
    const columns = idsOf('column');
    columns.forEach((c, i) => after(columns.length === footings.length ? [footings[i]!] : footings.length ? footings : piles.slice(-1), [c]));
    const front = idsOf('front-wall');
    after(footings.length ? footings : piles.slice(-1), front);
    const caps = idsOf('cap');
    after(columns.length ? columns : front, caps);
    const walls = idsOf('wall');
    after(front.length ? front : caps, walls);
    const bearings = idsOf('bearing');
    after(caps.length ? caps : front.length ? front : columns, bearings);
    const approach = idsOf('approach');
    after(walls.length ? walls : caps, approach);
    for (const other of idsOf('other')) after(caps.length ? caps : footings, [other]);
    bearingsDone.set(s.name, bearings.length ? bearings : caps.length ? caps : front.length ? front : columns);
    lastApproach = lastApproach.concat(approach);
  }

  /* ---- superstructure, one unit per span, girders erected in span order ---- */
  let lastGirder: string | undefined;
  const decks: string[] = [];
  spanNames.forEach((span, i) => {
    const list = items.filter((it) => it.unit === span);
    if (list.length === 0) return;
    const gid = group(span, sup);
    const names = named(list);
    const ids = new Map<Item, string>();
    for (const it of [...list].sort((a, b) => a.order - b.order)) ids.set(it, leaf(it, names.get(it)!, gid));
    const idsOf = (stage: Stage): string[] => byStage(list, stage).map((it) => ids.get(it)!);
    const supportsReady = [...(bearingsDone.get(supports[i]!.name) ?? []), ...(bearingsDone.get(supports[i + 1]!.name) ?? [])];

    const girders = idsOf('girder');
    for (const g of girders) {
      for (const b of supportsReady) fs(b, g);
      fs(lastGirder, g);
      lastGirder = g;
    }
    const diaphragms = idsOf('diaphragm');
    for (const d of diaphragms) for (const g of girders.length ? girders : supportsReady) fs(g, d);
    const deck = idsOf('deck');
    for (const d of deck) for (const b of diaphragms.length ? diaphragms : girders.length ? girders : supportsReady) fs(b, d);
    decks.push(...deck);
    const edges = [...idsOf('edge'), ...idsOf('other')];
    for (const e of edges) for (const d of deck.length ? deck : girders) fs(d, e);
  });

  /* ---- surfacing, across the deck ---- */
  const finishing = items.filter((it) => it.unit === 'Deck finishes');
  if (finishing.length > 0) {
    const gid = group('Deck finishes', root);
    const names = named(finishing);
    for (const it of finishing) {
      const id = leaf(it, names.get(it)!, gid);
      for (const d of [...decks, ...lastApproach]) fs(d, id);
    }
  }

  /* Drop empty groups — a bridge with no deck finishes has no such row. */
  const kept = activities.filter((a) => a.durationDays !== 0 || (a.children ?? []).length > 0);
  return { schedule: { activities: kept, links }, components };
}

/**
 * The programme of works on a bridge in service (LC-81): one activity, the works
 * themselves, over the contract's time for completion. A works baseline used to
 * freeze the model's whole new-build programme — piles, girders, deck — for a
 * joint and bearing rehabilitation.
 */
export function worksProgramme(name: string, workingDays: number): SerialisedSchedule {
  return {
    activities: [
      { id: 'A1000', name, parentId: null, children: ['A1010'], durationDays: 0, manualStartDays: null, componentKind: '' },
      { id: 'A1010', name, parentId: 'A1000', children: [], durationDays: Math.max(1, Math.round(workingDays)), manualStartDays: null, componentKind: 'Works' },
    ],
    links: [],
  };
}
